import React, { FC, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { AGGREGATOR_TYPE } from "../../../../data/outlet-status-constants";
import { getAggregatorLogo } from "../../../../helpers/accounting-helper";
import recon_reports_skeleton from "../../../../assets/images/recon-reports-skeleton.gif";
import {
  UPLOAD_STATUS_TYPES,
} from "../../../../data/accounting-constants";
import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS } from "../../../../data/constants";
import { dateToDDMMYY } from "../../../../helpers/moment-helper";
import CtaComponent from "../../../../components/reusable-ui/cta";
import { RECON_DATA, RECON_PENDING_LIST_TABLE_COLUMNS, RECON_REPORT_LIST_TABLE_COLUMNS, RECON_UPLOAD_LIST_TABLE_COLUMNS, ReconColumnKeys, ReconPendingColumnKeys, ReconUploadColumnKeys, recon_logs_default_limit } from "../constants";
import { ReconContext, ReportRowDataType, ReportsType } from "../recon-context";
import { Checkbox } from "@mui/material";
import ReconStatusChip, { ReconStatus } from "../reports-view/recon-status-chip";
import { ReconFileSummaryType, ReconReportStatusType } from "../recon-context/types";
import CommonPagination from "../../../../components/reusable-ui/common-pagination";
import moment from "moment";

const ReconReportsView: FC = () => {
  const { reconContextData: { reportsViewData: { reportType, uploadedReports, reports, selectedOutlets }, selectedReportWithoutFilter = [], filterData: { applied } }, fetchUploadedFiles, fetchOutletWiseReports } = useContext(ReconContext);
  const isShowCompletedReports = reportType === ReportsType.done;
  const paginationCount = applied ? reports.totalFiles : uploadedReports.totalFiles;
  const selectedCount = selectedOutlets.length || selectedReportWithoutFilter?.length;

  function fetchData(limit: number, page: number) {
    //based on filter applied or not, call function from context.
    applied ? fetchOutletWiseReports(limit, page) : fetchUploadedFiles(limit, page);
  }

  return <div className={`${isShowCompletedReports ? "mt-20" : ""} w-full overflow-hidden h-screen`}>
    <ReportsHeader />
    <div className="overflow-y-auto w-full bg-white rounded-[6px] " style={{ height: "calc(100% - 440px)" }}> 
    <table className="w-full border-collapse h-full">
        <ReportListTableHeader />
        <ReportListTableData />
    </table>
  </div>

  {!!paginationCount && !selectedCount && ( //do not show pagination when some reports are selected
    <CommonPagination
      getUpdatedRecords={fetchData}
      totalCount={paginationCount}
      limit={recon_logs_default_limit}
      containerClass="ml-auto"
    />
  )}
</div>
};


function RenderIntroScreen() {
  const { toggleUploadModal } = useContext(ReconContext);
  return (
    <tbody>
      <tr>
        <td colSpan={Object.keys(RECON_REPORT_LIST_TABLE_COLUMNS).length}>
          <div
            className={`flex flex-col items-center justify-center m-20 bg-white
        min-h-[600px] max-h-[calc(100vh-20px)] w-[calc(100%-40px)]
      `}
          >
            <div className="rista-sprite no-payout-files"></div>
            <div className="font-medium text-black/70 text-20 mt-[35px]">{RECON_DATA.reports.noFileHeader}</div>
            <div className="text-17 text-black/40 mt-[11px]">{RECON_DATA.reports.noFileDescription}</div>
            <CtaComponent
              ctaText="Upload Your File File"
              ctaSize={CTA_SIZE.medium}
              ctaType={CTA_TYPE.secondary_active_cta}
              onCtaClick={toggleUploadModal}
              customClass="mt-[35px] w-[374px]"
            />
          </div>
        </td>
      </tr>
    </tbody>
  );
}

function RenderNoFilerResultsScreen() {
  const { resetFilters } = useContext(ReconContext);
  return (
    <td className="p-0 h-full" colSpan={Object.keys(RECON_REPORT_LIST_TABLE_COLUMNS).length}>
  <div className="flex flex-col h-full">
    <div className="flex-grow flex flex-col items-center justify-center m-20 bg-white w-[calc(100%-40px)] mx-auto">
      <div className="rista-sprite no-report-filter"></div>
      <div className="font-medium text-black/70 text-20 mt-[35px]">{RECON_DATA.reports.noFilterResults}</div>
      <div className="text-17 text-black/40 mt-[11px]">{RECON_DATA.reports.noFilterResultsDescription}</div>
      <CtaComponent
        ctaText="Remove All Filters"
        ctaSize={CTA_SIZE.medium}
        ctaType={CTA_TYPE.secondary_active_cta}
        onCtaClick={resetFilters}
        customClass="mt-[35px] w-[181px]"
      />
    </div>
  </div>
</td>

  );
}

function ReportListTableData() {
  const { reconContextData, selectSingleReport, downloadAndEmailReport } = useContext(ReconContext);
  const {
    businessBranchesDataMap,
    allChannelsDefDataMap,
  } = useSelector((state: RootState) => ({
    businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
    allChannelsDefDataMap: state.businessReducer.allChannelsDefDataMap,
    uploadedReportsMetaData: state.accountingReducer.uploadedReportsMetaData,
  }));
  const isShowCompletedReports = reconContextData.reportsViewData.reportType === ReportsType.done;
  const { files: uploadedFiles, totalFiles: totalUploadedFiles } = reconContextData.reportsViewData.uploadedReports;
  const reportsData = reconContextData.reportsViewData.reports; //reconContextData.reportsViewData.pendingReports;
  const filterData = reconContextData.filterData;

  function renderRowCheckbox(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;
    const outletId = isFileType ? metaData?.fileWiseSummary?.outletId : metaData?.outletId
    const isFilterApplied = filterData.applied;

    const isSelected = isFilterApplied ? reconContextData.reportsViewData.selectedOutlets.includes(outletId) : 
    reconContextData?.selectedReportWithoutFilter?.includes((metaData as any).attachment.id);

    const customId = isFilterApplied ? outletId : (metaData as any).attachment.id;

    return <td className="pl-15" onChange={selectSingleReport.bind(null, customId, isFilterApplied )} ><Checkbox checked={isSelected} size={"small"} /></td>
  }

  function renderBranchChannelName(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;
    return (
      <td className="font-semibold pl-15">
        <div className="flex flex-col">
          {isFileType && <div>{businessBranchesDataMap[metaData?.branchId]?.branchName || "Invalid Branch"}</div>}
          <div className="text-black/60 text-12">{isFileType ? metaData?.fileWiseSummary?.outletId : metaData.outletId || ""}</div>
        </div>
      </td>
    );
  }

  function renderAggregatorInfo(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;;
    return (
      <td className="pl-30">
        <img
          src={getAggregatorLogo(isFileType ? metaData?.aggregatorType : metaData.aggregatorType)}
          alt=""
          width={24}
          height={24}
        />
      </td>
    );
  }

  function renderDateRange(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;
    let startDate, endDate;
    if (isFileType) {
      startDate = metaData?.fileWiseSummary?.firstDate;
      endDate = metaData?.fileWiseSummary?.lastDate;
    }
    else {
      startDate = metaData?.firstDate;
      endDate = metaData?.lastDate;
    }
    return (
      <td className="text-[#666666] pl-15">
        {moment(startDate?.slice(0,11), DATE_TIME_FORMATS.yearMonthDay1).format(DATE_TIME_FORMATS.dayMonthYear)}
        {"-"}
        {moment(endDate?.slice(0,11), DATE_TIME_FORMATS.yearMonthDay1).format(DATE_TIME_FORMATS.dayMonthYear)}
      </td>
    );
  }

  function renderCreatedDate(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;
    if (!isFileType) return <></>;
    return <td className="text-[#666666] pl-15">
      {dateToDDMMYY(metaData?.createdDate || "")}
    </td>
  }

  function renderFileData(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isFileType = 'fileWiseSummary' in metaData;
    if (!isFileType) return <></>
    return <td className="text-[#666666] pl-15">
      {metaData?.attachment?.fileName}
    </td>
  }

  function renderUploadStatus(metaData: ReportRowDataType) {
    return (
      <p
        className={`whitespace-nowrap ${metaData.reconStatus === UPLOAD_STATUS_TYPES.failed.type
          ? "text-[#AB3737] font-semibold"
          : ""
          }`}
      >
        {UPLOAD_STATUS_TYPES[metaData?.reconStatus]?.label}
      </p>
    );
  }

  function renderReconStatus(metaData: ReportRowDataType | ReconFileSummaryType) {
    let unmatchedOrderCount, notFoundOrderCount, matchedOrderCount;
    if ('fileWiseSummary' in metaData) {
      unmatchedOrderCount = metaData.fileWiseSummary.unmatchedOrderCount;
      matchedOrderCount = metaData.fileWiseSummary.matchedOrderCount;
      notFoundOrderCount = metaData.fileWiseSummary.notFoundOrderCount;
    }
    else {
      unmatchedOrderCount = metaData.unmatchedOrderCount;
      matchedOrderCount = metaData.matchedOrderCount;
      notFoundOrderCount = metaData.notFoundOrderCount;
    }
    return <td className="">
      <div className="flex gap-8 items-center">
        <ReconStatusChip count={matchedOrderCount} status={ReconStatus.noIssues} />
        <ReconStatusChip count={unmatchedOrderCount} status={ReconStatus.unmatched} />
        <ReconStatusChip count={notFoundOrderCount} status={ReconStatus.notFound} />
      </div>
    </td>
  }

  function renderSingleReportInfo(metaData: ReportRowDataType | ReconFileSummaryType) {
    const isRowType = 'fileWiseSummary' in metaData;
    const isSelected = isRowType ? reconContextData.reportsViewData.selectedOutlets.includes(metaData.mid) : false;

    function renderOrdersCount(metaData: ReportRowDataType | ReconFileSummaryType) {
      return <td className="pl-15">{'fileWiseSummary' in metaData ? metaData.fileWiseSummary?.totalOrders : metaData.totalOrders || 0}</td>
    }

    function renderAmount(metaData: ReportRowDataType | ReconFileSummaryType) {
      return <td className="pl-15">₹{'fileWiseSummary' in metaData ? metaData.fileWiseSummary?.totalOrdersAmount : metaData.totalOrdersAmount || 0}</td>
    }

    function renderVariance(metaData: ReportRowDataType | ReconFileSummaryType) {
      const difference = 'fileWiseSummary' in metaData ? metaData.fileWiseSummary?.payoutDifference : metaData.payoutDifference;
      const textColor = difference > 0 ? "#089634" : (difference < 0 ? "#AF0000" : "#000000DE");
      return <td className={`pl-15`} style={{ color: textColor }}>₹{difference || 0}</td>
    }

    function renderActions(metaData: ReportRowDataType | ReconFileSummaryType) {
      const isFileType = 'fileWiseSummary' in metaData;
      const downloadLink = isFileType ? metaData.links?.url : '';
      const isFilterApplied = filterData.applied
      const outletId = isFileType ? metaData?.fileWiseSummary?.outletId : metaData?.outletId
      const customId = isFilterApplied ? outletId : (metaData as any).attachment.id;

      function checkDownloadLink() {
        if (downloadLink?.length) { // check if download link is available, do not need to hit api
          window.open(downloadLink, '_blank');
        }
        else {
          downloadAndEmailReport(true, customId, true);
        }
      }
  
      return <td className="pl-15 group">
        <div className="flex space-x-8 text-activeColor font-medium text-13">
          <div onClick={checkDownloadLink} className="cursor-pointer">Download</div>
          <div onClick={downloadAndEmailReport.bind(null, false, customId, true)} className="cursor-pointer">Email</div>
        </div>
      </td>
    }

    return (
      <tr
        className={`text-13 py-14 border-b border-[#DEDEDF] text-black/[87] h-[64px] ${isSelected ? "bg-[#F2F8FF]" : ""}`}
        key={isRowType ? metaData?.mid : metaData.outletId}
      >
        {isShowCompletedReports  && renderRowCheckbox(metaData)}
        {!isShowCompletedReports && renderCreatedDate(metaData)}
        {isShowCompletedReports && renderDateRange(metaData)}
        {renderAggregatorInfo(metaData)}
        {renderBranchChannelName(metaData)}
        {isShowCompletedReports && renderOrdersCount(metaData)}
        {isShowCompletedReports && renderAmount(metaData)}
        {isShowCompletedReports && renderReconStatus(metaData)}
        {isShowCompletedReports && renderVariance(metaData)}
        {isShowCompletedReports && renderActions(metaData)}
        {!isShowCompletedReports && renderFileData(metaData)}
      </tr>
    );
  }

  if (reconContextData.reportsViewData.loading) return <RenderSkeletonLoader />;

  if (!totalUploadedFiles && !filterData.applied) return <RenderIntroScreen />

  if (!reportsData?.files.length && filterData.applied) return <RenderNoFilerResultsScreen />

  return <tbody className="overflow-y-auto ">
    {filterData.applied ? reportsData?.files?.map((metaData: ReconFileSummaryType) =>
      renderSingleReportInfo(metaData)
    ) : uploadedFiles.map((metaData: ReportRowDataType) =>
      renderSingleReportInfo(metaData))}
  </tbody>
}

function ReportListTableHeader() {
  const { reconContextData: { reportsViewData, filterData, selectedReportWithoutFilter = [] }, toggleSelectAllReports } = useContext(ReconContext);
  const isShowCompletedReports = reportsViewData.reportType === ReportsType.done;

  const allSelected = useMemo(() => {
    return reportsViewData.selectedOutlets.length > 0 && (reportsViewData.selectedOutlets.length === reportsViewData.reports.files?.length)
  }, [reportsViewData]);

  const allUnfilteredReportsSelected = useMemo(() => {
    if(reportsViewData.uploadedReports.files?.length === 0) return false
    return selectedReportWithoutFilter?.length === reportsViewData.uploadedReports.files?.length
  }, [selectedReportWithoutFilter, reportsViewData.uploadedReports.files])

  const someSelected = useMemo(() => {
    return reportsViewData.selectedOutlets.length > 0
  }, [reportsViewData.selectedOutlets]);

  function renderSelectAllCheckbox() {
    const isChecked = filterData.applied ? allSelected : allUnfilteredReportsSelected
    const someSelectedDetails = filterData.applied ? someSelected : selectedReportWithoutFilter?.length > 0
    return <Checkbox size={"small"}
      indeterminate={someSelectedDetails && !allSelected && !allUnfilteredReportsSelected}
      checked={isChecked}
      onClick={toggleSelectAllReports.bind(null, someSelectedDetails ? false : true)} />
  }

  function renderCompletedColumns() {
    const columns: any = RECON_REPORT_LIST_TABLE_COLUMNS;
    return Object.keys(columns).map((key) => {
      const typedKey = key as ReconColumnKeys;
      return (
        <td key={typedKey} className="pl-15" id={typedKey}>
          {typedKey === 'checkBox' ? renderSelectAllCheckbox() : columns[typedKey]}
        </td>
      );
    })
  }

  function renderPendingColumns() {
    const columns = RECON_PENDING_LIST_TABLE_COLUMNS;
    return Object.keys(columns).map((key) => {
      const typedKey = key as ReconPendingColumnKeys;
      return (
        <td key={typedKey} className="pl-15" id={typedKey}>
          {columns[typedKey]}
        </td>
      );
    })
  }

  return <thead className="bg-greyF6 text-[#7F7F7F] font-medium text-12 h-[53px] sticky top-0 z-10">
    <tr>{isShowCompletedReports ? renderCompletedColumns() : renderPendingColumns()}</tr>
  </thead>
}

function ReportsHeader() {
  const { reconContextData: { reportsViewData, filterData: { applied }, selectedReportWithoutFilter }, toggleSelectAllReports, toggleReconLogs, setReportViewType } = useContext(ReconContext);
  const isShowCompletedReports = reportsViewData.reportType === ReportsType.done;
  const selectedCount = reportsViewData.selectedOutlets.length || selectedReportWithoutFilter?.length;

  function HeaderActions() {
    const { downloadAndEmailReport } = useContext(ReconContext);

    function handleDownload() {
      downloadAndEmailReport(true);
    }
    function handleEmail() {
      downloadAndEmailReport(false);
    }

    return <div className={`flex items-center mt-[28px] mb-[24px] rounded-[28px] text-blue font-medium py-[3px] pl-8 pr-[40px] bg-white ${isShowCompletedReports ? "" : "mb-[20px]"}`}>
      <div onClick={toggleSelectAllReports.bind(null, false)} className="cursor-pointer rista-sprite ic-cross-grey-big"></div>
      <div className="text-black/[87] font-normal ml-[5px]">{selectedCount} Selected</div>
      <div className="flex items-center gap-[42px] ml-auto">
        <div className="cursor-pointer flex items-center gap-8 border-r pr-[42px] border-[#BCBCBC99]" onClick={toggleSelectAllReports.bind(null, true)}><div className="rista-sprite select-all-icon"></div>Select All</div>
        <div className="cursor-pointer flex items-center gap-8" onClick={handleEmail}><div className="rista-sprite ic-export-blue "></div>Email Report</div>
        <div className="cursor-pointer flex items-center gap-8" onClick={handleDownload}><div className="rista-sprite ic-download-sm-blue"></div>Download Report</div>
      </div>
    </div>
  }

  if (selectedCount) return <HeaderActions />;
  return <div className={``}>
    <div className={`flex mx-[8px] items-center my-[20px] ${isShowCompletedReports ? '' : 'mt-[20px]'} `}>
      <div className="flex items-center font-semibold text-20">{isShowCompletedReports ? RECON_DATA.reports.reportHeader : RECON_DATA.reports.reportHeaderDiscrepancy}</div>
      {applied && <div className="text-20 text-black/50 ml-12">(Filter Results)</div>}
      {/* {!isShowCompletedReports && <div className="ml-12 px-12 py-5 rounded-[18px] bg-white text-14 text-black/80">{reportsToRendered.length} Pending</div>} */}
      {isShowCompletedReports && <>
        <div title={RECON_DATA.reports.checkLogsTooptip} className="cursor-pointer ml-auto flex items-center gap-12">
          <div className="rista-sprite time-icon"></div>
          <div className="font-medium text-16 text-activeColor" onClick={toggleReconLogs}>{RECON_DATA.reports.checkLogs}</div>
        </div>
        <div className="rista-sprite ic-question ml-[52px]"></div><div className="font-medium text-16 text-activeColor ml-[12px]">{RECON_DATA.reports.howIsCalculated}</div></>}
    </div>
    {!isShowCompletedReports && <div className="bg-white px-20 py-[25px] mt-[24px] rounded-t-[6px]">
      <div className="cursor-pointer text-activeColor flex items-center gap-[7px] font-medium" onClick={setReportViewType.bind(null, ReportsType.done)}> <div className="rista-sprite back-blue-icon"></div>Back</div>
    </div>}
  </div>
}

function RenderSkeletonLoader() {
  return (
    <tbody>
      <td colSpan={Object.keys(RECON_REPORT_LIST_TABLE_COLUMNS).length}>
        <img
          src={recon_reports_skeleton}
          alt="skeleton"
          className="w-[calc(100%-40px)] bg-white m-20 mb-0"
        />
      </td>
    </tbody>);
}

export default ReconReportsView;
